import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState } from 'recoil';
import dayjs from 'dayjs';
import {
  Table,
  Tag,
  Spin,
  Empty,
  Input,
  Select as AntSelect,
  Modal,
  Alert,
  Button
} from 'antd';
import { Map as GoogleMap, useMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBusAlt, faCar, faQuestionCircle, faChevronUp, faExclamationTriangle, faSyncAlt } from '@fortawesome/pro-duotone-svg-icons';
import {
  notificationsAtom,
  amaDashboardCurrentTabAtom,
  amaDashboardLiveMapSettingsAtom,
  amaDashboardShowMphAtom
} from '../../../../atoms/Atoms';
import { createLiveFleetConnection, stopLiveFleetConnection } from '../../../../api/liveFleetHub';
import { loadGtfsCore, loadStopTimes } from './otp/gtfsLoader';
import {
  classifyVehicleType,
  inferShapeForVehicle,
  classifyNextStopOtp,
  aggregateRouteOtp,
  haversineMeters,
  gtfsTimeToMinutes,
  nowMinutesSinceMidnight,
  Statuses,
  ON_TIME_THRESHOLD_EARLY_MIN,
  ON_TIME_THRESHOLD_LATE_MIN
} from './otp/otpCalculations';
import { convertKmhToMph, thousand } from '../../../../functions';

const PR_CENTER = { lat: 18.3920497, lng: -66.084423 };
const PR_DEFAULT_ZOOM = 12;
const FOCUS_ZOOM = 16;
const STOPPED_SPEED_THRESHOLD_KMH = 1;

const STATUS_COLORS = {
  [Statuses.OnTime]: '#16a34a',
  [Statuses.Early]: '#f59e0b',
  [Statuses.Delayed]: '#dc2626'
};
const STATUS_COLOR_DEFAULT = '#6b7280';

const VEHICLE_TYPE_ICONS = {
  bus: faBusAlt,
  car: faCar,
  other: faQuestionCircle
};

// Light, minimalist map style. All POIs are hidden so the only things on the
// map are the road network, water, parks, and locality labels — the rest is
// drawn by our own markers and polylines on top. Applied via the `styles`
// prop on <GoogleMap>; if a real Cloud-configured Map ID is set, Google Maps
// will ignore this array and use the cloud style instead.
const MAP_STYLES = [
  // Hide every kind of point of interest.
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.attraction', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.place_of_worship', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.sports_complex', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.medical', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.government', stylers: [{ visibility: 'off' }] },
  // Transit icons clutter the map without adding value when our own vehicle
  // markers and stops are on top of them.
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },

  // Soft cream background.
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f5f5f3' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#eeeeec' }] },

  // Subdued water.
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d6e4ec' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7a8a96' }] },

  // Roads — white with subtle strokes; highways slightly darker so they stand
  // out from arterials.
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e5e5e5' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e7e7e3' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#cccccc' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#fafafa' }] },

  // Only major locality labels remain; neighbourhoods get hidden.
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#404040' }] },
  { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },

  // Parks rendered as a faint green wash, no labels.
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#e0eadc' }, { visibility: 'on' }] },
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] }
];

function AmaDashboardLiveMap() {
  const { t } = useTranslation();
  const currentTab = useRecoilValue(amaDashboardCurrentTabAtom);
  const mapSettings = useRecoilValue(amaDashboardLiveMapSettingsAtom);
  const showMph = useRecoilValue(amaDashboardShowMphAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const isActiveTab = currentTab === '1';

  const [vehicles, setVehicles] = useState([]);
  const [gtfs, setGtfs] = useState(null);
  const [stopTimes, setStopTimes] = useState(null);
  const [loadingGtfs, setLoadingGtfs] = useState(true);
  const [loadingStopTimes, setLoadingStopTimes] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);

  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState('all');
  const [modalVehicleId, setModalVehicleId] = useState(null);
  // Session-only banner dismissal: the alert reappears whenever the user
  // navigates back to this page so they're reminded the data is estimated.
  const [disclaimerDismissed, setDisclaimerDismissed] = useState(false);
  // Bumped by the reconnect button to force the SignalR effect to tear down
  // the dead connection and create a new one.
  const [reconnectTick, setReconnectTick] = useState(0);

  const hubRef = useRef(null);
  const polylinesRef = useRef([]);
  const mapContainerRef = useRef(null);
  const googleMap = useMap('ama-live-map');

  const pushError = (description) => {
    setNotifications([...notifications, { title: t('error'), description, error: true }]);
  };

  useEffect(() => {
    let cancelled = false;
    loadGtfsCore()
      .then((data) => { if (!cancelled) setGtfs(data); })
      .catch((err) => { if (!cancelled) pushError(err.message); })
      .finally(() => { if (!cancelled) setLoadingGtfs(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isActiveTab) {
      if (hubRef.current) {
        stopLiveFleetConnection(hubRef.current);
        hubRef.current = null;
        setConnectionStatus('disconnected');
      }
      return undefined;
    }
    hubRef.current = createLiveFleetConnection({
      onSnapshot: (snapshot) => setVehicles(Array.isArray(snapshot) ? snapshot : []),
      onError: (err) => pushError(err.message || String(err)),
      onStatusChange: setConnectionStatus
    });
    return () => {
      if (hubRef.current) {
        stopLiveFleetConnection(hubRef.current);
        hubRef.current = null;
      }
    };
  }, [isActiveTab, reconnectTick]);

  useEffect(() => {
    if (!gtfs || stopTimes || loadingStopTimes) return;
    if (vehicles.length === 0) return;
    setLoadingStopTimes(true);
    loadStopTimes()
      .then(setStopTimes)
      .catch((err) => pushError(err.message))
      .finally(() => setLoadingStopTimes(false));
  }, [gtfs, vehicles, stopTimes]);

  // Indexes built once when GTFS / stopTimes load.
  const tripsByShape = useMemo(() => {
    if (!gtfs) return null;
    const m = new Map();
    for (const trip of gtfs.trips) {
      if (!trip.shapeId) continue;
      if (!m.has(trip.shapeId)) m.set(trip.shapeId, []);
      m.get(trip.shapeId).push(trip);
    }
    return m;
  }, [gtfs]);

  const stopsById = useMemo(() => {
    if (!gtfs) return null;
    return new Map(gtfs.stops.map((s) => [s.stopId, s]));
  }, [gtfs]);

  const stopTimesByTrip = useMemo(() => {
    if (!stopTimes) return null;
    const m = new Map();
    for (const st of stopTimes) {
      if (!m.has(st.tripId)) m.set(st.tripId, []);
      m.get(st.tripId).push(st);
    }
    m.forEach((arr) => arr.sort((a, b) => a.sequence - b.sequence));
    return m;
  }, [stopTimes]);

  const stopIdsByRoute = useMemo(() => {
    if (!gtfs || !stopTimesByTrip) return null;
    const m = new Map();
    for (const trip of gtfs.trips) {
      if (!trip.routeId) continue;
      const sts = stopTimesByTrip.get(trip.tripId);
      if (!sts) continue;
      if (!m.has(trip.routeId)) m.set(trip.routeId, new Set());
      const set = m.get(trip.routeId);
      for (const st of sts) set.add(st.stopId);
    }
    return m;
  }, [gtfs, stopTimesByTrip]);

  const liveAssignments = useMemo(() => {
    if (!gtfs || !tripsByShape) return [];
    const nowMin = nowMinutesSinceMidnight();
    return vehicles.map((v) => {
      const vehicleType = classifyVehicleType(v.name);
      if (vehicleType !== 'bus') {
        return { vehicle: v, vehicleType, routeId: null, shapeId: null, nextStop: null, otp: null };
      }
      const vp = { lat: v.lat, lng: v.lng, speedKmh: v.speedKmh };
      const shapeMatch = inferShapeForVehicle(vp, gtfs.shapes);
      if (!shapeMatch) {
        return { vehicle: v, vehicleType, routeId: null, shapeId: null, nextStop: null, otp: { status: Statuses.OnTime, deltaMinutes: 0 } };
      }
      const tripsOnShape = tripsByShape.get(shapeMatch.shape.shapeId) || [];
      let routeId = null;
      if (tripsOnShape.length > 0) {
        const counts = new Map();
        for (const trip of tripsOnShape) counts.set(trip.routeId, (counts.get(trip.routeId) || 0) + 1);
        let bestCount = -1;
        counts.forEach((c, r) => { if (c > bestCount) { bestCount = c; routeId = r; } });
      }

      let nextStop = null;
      if (stopTimesByTrip && stopsById) {
        let bestDist = Infinity;
        for (const trip of tripsOnShape) {
          const sts = stopTimesByTrip.get(trip.tripId);
          if (!sts) continue;
          for (const st of sts) {
            const schedMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
            if (schedMin === null || schedMin < nowMin - 2) continue;
            const stop = stopsById.get(st.stopId);
            if (!stop) continue;
            const d = haversineMeters({ lat: v.lat, lng: v.lng }, stop);
            if (d < bestDist) {
              bestDist = d;
              nextStop = { stop, scheduledMinutes: schedMin, distanceM: d, tripId: trip.tripId };
            }
          }
        }
      }

      const otp = nextStop
        ? classifyNextStopOtp({ vehicle: vp, nextStop })
        : { status: Statuses.OnTime, deltaMinutes: 0 };
      return { vehicle: v, vehicleType, routeId, shapeId: shapeMatch.shape.shapeId, nextStop, otp };
    });
  }, [vehicles, gtfs, tripsByShape, stopTimesByTrip, stopsById]);

  const filteredAssignments = useMemo(() => {
    const needle = vehicleSearch.trim().toLowerCase();
    return liveAssignments.filter((a) => {
      if (vehicleTypeFilter !== 'all' && a.vehicleType !== vehicleTypeFilter) return false;
      if (!needle) return true;
      const name = (a.vehicle.name || '').toLowerCase();
      const route = (a.routeId || '').toString().toLowerCase();
      return name.includes(needle) || route.includes(needle);
    });
  }, [liveAssignments, vehicleTypeFilter, vehicleSearch]);

  const tableData = useMemo(() => {
    const aggs = aggregateRouteOtp(
      liveAssignments
        .filter((a) => a.routeId)
        .map((a) => ({ routeId: a.routeId, vehicleName: a.vehicle.name, status: a.otp.status }))
    );
    return aggs.map((a) => ({
      key: a.routeId,
      routeId: a.routeId,
      routeName: gtfs?.routes.find((r) => r.routeId === a.routeId)?.shortName || a.routeId,
      assignedVehicles: a.assignedVehicles,
      totalTrips: a.totalTrips,
      onTimeCount: a.onTimeCount,
      earlyCount: a.earlyCount,
      delayedCount: a.delayedCount,
      otpPercentage: a.otpPercentage,
      trips: liveAssignments
        .filter((la) => la.routeId === a.routeId)
        .map((la) => ({
          tripId: `${la.vehicle.id}-live`,
          vehicleName: la.vehicle.name,
          terminalName: la.nextStop?.stop?.name,
          status: la.otp.status,
          delayMinutes: Math.round(la.otp.deltaMinutes)
        }))
    }));
  }, [liveAssignments, gtfs]);

  const overallStats = useMemo(() => {
    const total = tableData.reduce((s, r) => s + (r.totalTrips || 0), 0);
    const onTime = tableData.reduce((s, r) => s + (r.onTimeCount || 0), 0);
    const late = tableData.reduce((s, r) => s + (r.delayedCount || 0), 0);
    const early = tableData.reduce((s, r) => s + (r.earlyCount || 0), 0);
    const pct = (n) => (total === 0 ? 0 : Math.round((n / total) * 100));
    return {
      total,
      onTimeCount: onTime,
      lateCount: late,
      earlyCount: early,
      onTimePercent: pct(onTime),
      latePercent: pct(late),
      earlyPercent: pct(early)
    };
  }, [tableData]);

  // Imperative polyline rendering — declarative <Polyline/> is not exposed by
  // @vis.gl/react-google-maps, so we mirror AmaDashboardMap's approach.
  useEffect(() => {
    if (!googleMap || !gtfs || !tripsByShape) return undefined;
    const lines = polylinesRef.current;
    lines.forEach((p) => p.setMap(null));
    polylinesRef.current = [];

    for (const shape of gtfs.shapes) {
      const trip = tripsByShape.get(shape.shapeId)?.[0];
      const isSelected = trip && trip.routeId === selectedRouteId;
      const route = trip ? gtfs.routes.find((r) => r.routeId === trip.routeId) : null;
      const polyline = new window.google.maps.Polyline({
        path: shape.points,
        geodesic: true,
        strokeColor: route?.color || (isSelected ? '#1d4ed8' : '#64748b'),
        strokeOpacity: isSelected ? 0.9 : 0.45,
        strokeWeight: isSelected ? 5 : 3,
        clickable: true,
        map: googleMap
      });
      polyline.addListener('click', () => {
        if (trip) {
          setSelectedRouteId(trip.routeId);
          setSelectedStop(null);
        }
      });
      polylinesRef.current.push(polyline);
    }

    return () => {
      polylinesRef.current.forEach((p) => p.setMap(null));
      polylinesRef.current = [];
    };
  }, [googleMap, gtfs, tripsByShape, selectedRouteId]);

  const fitMapToRoute = (routeId) => {
    if (!googleMap || !gtfs || !routeId) return;
    const shapeIds = new Set();
    for (const trip of gtfs.trips) {
      if (trip.routeId === routeId && trip.shapeId) shapeIds.add(trip.shapeId);
    }
    if (shapeIds.size === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    for (const shape of gtfs.shapes) {
      if (!shapeIds.has(shape.shapeId)) continue;
      for (const point of shape.points) bounds.extend(point);
    }
    if (!bounds.isEmpty()) googleMap.fitBounds(bounds, 60);
  };

  const visibleStops = useMemo(() => {
    if (!gtfs || !selectedRouteId || !stopIdsByRoute) return [];
    const stopIds = stopIdsByRoute.get(selectedRouteId);
    if (!stopIds) return [];
    return gtfs.stops.filter((s) => stopIds.has(s.stopId));
  }, [gtfs, stopIdsByRoute, selectedRouteId]);

  const liveAssignmentForSelectedRoute = useMemo(() => {
    if (!selectedRouteId) return null;
    return liveAssignments.find((a) => a.routeId === selectedRouteId) || null;
  }, [liveAssignments, selectedRouteId]);

  const stopStatusColor = (stop) => {
    if (!liveAssignmentForSelectedRoute) return '#64748b';
    const isNext = liveAssignmentForSelectedRoute.nextStop?.stop?.stopId === stop.stopId;
    if (!isNext) return '#94a3b8';
    return STATUS_COLORS[liveAssignmentForSelectedRoute.otp.status] || '#64748b';
  };

  const renderStatusTag = (status) => {
    const color = status === Statuses.OnTime
      ? 'green'
      : status === Statuses.Early
        ? 'blue'
        : status === Statuses.Delayed
          ? 'red'
          : 'default';
    const label = t(status === Statuses.OnTime ? 'on_time' : status);
    return <Tag color={color}>{label}</Tag>;
  };

  const tableColumns = [
    {
      title: t('route'),
      dataIndex: 'routeName',
      key: 'routeName',
      render: (text, row) => (
        <button
          type="button"
          className="text-blue-600 hover:underline text-left"
          onClick={() => {
            setSelectedRouteId(row.routeId);
            setSelectedStop(null);
            fitMapToRoute(row.routeId);
            mapContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {text}
        </button>
      )
    },
    {
      title: t('assigned_vehicles'),
      dataIndex: 'assignedVehicles',
      key: 'assignedVehicles',
      render: (vehiclesList) => (vehiclesList && vehiclesList.length > 0)
        ?  _.map(vehiclesList, (v) => <Tag>{v}</Tag>)
        : <span className="text-gray-400">—</span>
    },
    {
      title: t('total_trips'),
      dataIndex: 'totalTrips',
      key: 'totalTrips',
      width: 110,
      render: (count) => thousand(count)
    },
    { title: t('on_time'), dataIndex: 'onTimeCount', key: 'onTimeCount', width: 100 },
    { title: t('delayed'), dataIndex: 'delayedCount', key: 'delayedCount', width: 100 },
    {
      title: t('otp_percentage'),
      dataIndex: 'otpPercentage',
      key: 'otpPercentage',
      width: 110,
      render: (pct) => `${pct}%`
    }
  ];

  const expandedRowRender = (row) => {
    const tripColumns = [
      { title: t('vehicle'), dataIndex: 'vehicleName', key: 'vehicleName' },
      { title: t('next_stop'), dataIndex: 'terminalName', key: 'terminalName' },
      { title: t('status'), dataIndex: 'status', key: 'status', render: renderStatusTag },
      {
        title: t('delay_minutes'),
        dataIndex: 'delayMinutes',
        key: 'delayMinutes',
        render: (m) => (m === null || m === undefined ? '—' : `${m}`)
      }
    ];
    return (
      <Table
        size="small"
        rowKey="tripId"
        columns={tripColumns}
        dataSource={row.trips || []}
        pagination={false}
      />
    );
  };

  const formatSpeed = (kmh) => {
    if (kmh === null || kmh === undefined) return '—';
    return showMph
      ? `${convertKmhToMph(kmh).toFixed(1)} mph`
      : `${kmh.toFixed(1)} km/h`;
  };

  const focusVehicle = (vehicle) => {
    if (!vehicle) return;
    if (googleMap && Number.isFinite(vehicle.lat) && Number.isFinite(vehicle.lng)) {
      googleMap.panTo({ lat: vehicle.lat, lng: vehicle.lng });
      googleMap.setZoom(FOCUS_ZOOM);
    }
    setSelectedStop(null);
    setModalVehicleId(vehicle.id);
  };

  const modalAssignment = useMemo(
    () => (modalVehicleId ? liveAssignments.find((x) => x.vehicle.id === modalVehicleId) : null),
    [modalVehicleId, liveAssignments]
  );

  const modalUpcomingStops = useMemo(() => {
    if (!modalAssignment || !modalAssignment.shapeId || !modalAssignment.routeId) return [];
    if (!tripsByShape || !stopTimesByTrip || !stopsById) return [];
    const tripsOnRoute = (tripsByShape.get(modalAssignment.shapeId) || [])
      .filter((trip) => trip.routeId === modalAssignment.routeId);
    if (tripsOnRoute.length === 0) return [];

    const v = modalAssignment.vehicle;
    const nowMin = nowMinutesSinceMidnight();
    let bestTripId = null;
    let bestDistance = Infinity;

    for (const trip of tripsOnRoute) {
      const sts = stopTimesByTrip.get(trip.tripId);
      if (!sts) continue;
      for (const st of sts) {
        const schedMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
        if (schedMin === null || schedMin < nowMin - 2) continue;
        const stop = stopsById.get(st.stopId);
        if (!stop) continue;
        const d = haversineMeters({ lat: v.lat, lng: v.lng }, stop);
        if (d < bestDistance) {
          bestDistance = d;
          bestTripId = trip.tripId;
        }
        break;
      }
    }
    if (!bestTripId) return [];

    const sts = stopTimesByTrip.get(bestTripId) || [];
    const speedKmh = Math.max(v.speedKmh || 0, 0);
    const speedMps = speedKmh < STOPPED_SPEED_THRESHOLD_KMH ? 0 : (speedKmh * 1000) / 3600;
    let cumulativeDist = 0;
    let prevPos = { lat: v.lat, lng: v.lng };
    const upcoming = [];

    for (const st of sts) {
      const schedMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
      if (schedMin === null || schedMin < nowMin - 2) continue;
      const stop = stopsById.get(st.stopId);
      if (!stop) continue;
      cumulativeDist += haversineMeters(prevPos, stop);
      prevPos = { lat: stop.lat, lng: stop.lng };

      const etaSeconds = speedMps > 0 ? cumulativeDist / speedMps : null;
      const etaMin = etaSeconds === null ? null : nowMin + etaSeconds / 60;
      const deltaMin = etaMin === null ? null : etaMin - schedMin;
      let status;
      if (deltaMin === null) status = null;
      else if (deltaMin < -ON_TIME_THRESHOLD_EARLY_MIN) status = Statuses.Early;
      else if (deltaMin > ON_TIME_THRESHOLD_LATE_MIN) status = Statuses.Delayed;
      else status = Statuses.OnTime;

      upcoming.push({
        key: `${st.tripId}-${st.sequence}-${st.stopId}`,
        stopName: stop.name,
        scheduledMinutes: schedMin,
        etaMinutesFromNow: etaSeconds === null ? null : etaSeconds / 60,
        status
      });
    }
    return upcoming;
  }, [modalAssignment, tripsByShape, stopTimesByTrip, stopsById]);

  const stopModalNextBus = useMemo(() => {
    if (!selectedStop || !gtfs || !tripsByShape || !stopTimesByTrip || !stopsById) return null;
    const nowMin = nowMinutesSinceMidnight();
    let best = null;

    for (const a of liveAssignments) {
      if (a.vehicleType !== 'bus' || !a.shapeId || !a.routeId) continue;
      const v = a.vehicle;

      const tripsOnRoute = (tripsByShape.get(a.shapeId) || [])
        .filter((trip) => trip.routeId === a.routeId);
      if (tripsOnRoute.length === 0) continue;

      let bestTripId = null;
      let bestStopDistance = Infinity;
      for (const trip of tripsOnRoute) {
        const sts = stopTimesByTrip.get(trip.tripId);
        if (!sts) continue;
        for (const st of sts) {
          const schedMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
          if (schedMin === null || schedMin < nowMin - 2) continue;
          const stop = stopsById.get(st.stopId);
          if (!stop) continue;
          const d = haversineMeters({ lat: v.lat, lng: v.lng }, stop);
          if (d < bestStopDistance) {
            bestStopDistance = d;
            bestTripId = trip.tripId;
          }
          break;
        }
      }
      if (!bestTripId) continue;

      const sts = stopTimesByTrip.get(bestTripId) || [];
      const speedKmh = Math.max(v.speedKmh || 0, 0);
      const speedMps = speedKmh < STOPPED_SPEED_THRESHOLD_KMH ? 0 : (speedKmh * 1000) / 3600;
      let cumulativeDist = 0;
      let prevPos = { lat: v.lat, lng: v.lng };

      for (const st of sts) {
        const schedMin = gtfsTimeToMinutes(st.arrivalTime || st.departureTime);
        if (schedMin === null || schedMin < nowMin - 2) continue;
        const stop = stopsById.get(st.stopId);
        if (!stop) continue;
        cumulativeDist += haversineMeters(prevPos, stop);
        prevPos = { lat: stop.lat, lng: stop.lng };

        if (st.stopId !== selectedStop.stopId) continue;

        const etaSeconds = speedMps > 0 ? cumulativeDist / speedMps : null;
        const etaMin = etaSeconds === null ? null : etaSeconds / 60;
        const deltaMin = etaMin === null ? null : nowMin + etaMin - schedMin;
        let status;
        if (deltaMin === null) status = null;
        else if (deltaMin < -ON_TIME_THRESHOLD_EARLY_MIN) status = Statuses.Early;
        else if (deltaMin > ON_TIME_THRESHOLD_LATE_MIN) status = Statuses.Delayed;
        else status = Statuses.OnTime;

        const candidate = {
          vehicle: v,
          routeId: a.routeId,
          scheduledMinutes: schedMin,
          etaMinutesFromNow: etaMin,
          status
        };

        const candidateRank = candidate.etaMinutesFromNow ?? Number.POSITIVE_INFINITY;
        const bestRank = best?.etaMinutesFromNow ?? Number.POSITIVE_INFINITY;
        if (!best || candidateRank < bestRank) best = candidate;
        break;
      }
    }
    return best;
  }, [selectedStop, liveAssignments, gtfs, tripsByShape, stopTimesByTrip, stopsById]);

  const formatGtfsClock = (minutesFromMidnight) => {
    if (minutesFromMidnight === null || minutesFromMidnight === undefined) return '—';
    const totalSecs = Math.floor(minutesFromMidnight * 60);
    const h24 = Math.floor(totalSecs / 3600) % 24;
    const m = Math.floor((totalSecs % 3600) / 60);
    const ampm = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 === 0 ? 12 : (h24 > 12 ? h24 - 12 : h24);
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const formatEta = (minutes) => {
    if (minutes === null || minutes === undefined) return t('stopped');
    if (minutes < 1) return t('arriving_now');
    return `${Math.round(minutes)} min`;
  };

  return (
    <>
      {!disclaimerDismissed && (
        <Alert
          type="warning"
          showIcon
          closable
          onClose={() => setDisclaimerDismissed(true)}
          message={t('otp_estimate_disclaimer')}
          className="mx-2 mb-2"
        />
      )}
      <div className="flex flex-wrap items-center gap-4 px-2 pb-2">
        <Tag color={connectionStatus === 'connected' ? 'green' : connectionStatus === 'reconnecting' ? 'orange' : 'red'}>
          {t(`signalr_${connectionStatus}`, connectionStatus)}
        </Tag>
        {connectionStatus === 'disconnected' && (
          <Button
            size="small"
            onClick={() => setReconnectTick((n) => n + 1)}
            icon={<FontAwesomeIcon icon={faSyncAlt} />}
          >
            {t('reconnect')}
          </Button>
        )}
      </div>

      <div
        className="grid gap-3 px-2 pb-2"
        style={{ gridTemplateColumns: '280px minmax(0, 1fr) 300px', height: 600 }}
      >
        {/* LEFT column: headline stats + per-route bar chart */}
        <div className="flex flex-col gap-3 overflow-hidden">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('on_time')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.OnTime] }}>
                {overallStats.onTimePercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.onTimeCount)}</div>
            </div>
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('early')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.Early] }}>
                {overallStats.earlyPercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.earlyCount)}</div>
            </div>
            <div className="bg-white rounded shadow-sm p-2 text-center">
              <div className="text-xs text-gray-500">{t('delayed')}</div>
              <div className="text-2xl font-bold" style={{ color: STATUS_COLORS[Statuses.Delayed] }}>
                {overallStats.latePercent}%
              </div>
              <div className="text-xs text-gray-400">{thousand(overallStats.lateCount)}</div>
            </div>
          </div>
          <div className="bg-white rounded shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
            <div className="text-sm font-semibold p-2 border-b">{t('routes')}</div>
            <div className="flex-1 overflow-y-auto p-2">
              {tableData.length === 0 ? (
                <div className="text-xs text-gray-400 text-center py-4">{t('no_data')}</div>
              ) : (
                tableData.map((row) => {
                  const total = row.totalTrips || 1;
                  const onTimePct = (row.onTimeCount / total) * 100;
                  const latePct = (row.delayedCount / total) * 100;
                  const earlyPct = (row.earlyCount / total) * 100;
                  const isSelected = row.routeId === selectedRouteId;
                  return (
                    <button
                      type="button"
                      key={row.routeId}
                      onClick={() => {
                        setSelectedRouteId(row.routeId);
                        setSelectedStop(null);
                        fitMapToRoute(row.routeId);
                      }}
                      className={`flex items-center gap-2 my-1 w-full text-left px-1 py-0.5 rounded hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <div className="w-12 text-xs truncate" title={row.routeName}>{row.routeName}</div>
                      <div className="flex-1 flex h-3 rounded overflow-hidden bg-gray-100">
                        <div style={{ width: `${onTimePct}%`, background: STATUS_COLORS[Statuses.OnTime] }} />
                        <div style={{ width: `${earlyPct}%`, background: STATUS_COLORS[Statuses.Early] }} />
                        <div style={{ width: `${latePct}%`, background: STATUS_COLORS[Statuses.Delayed] }} />
                      </div>
                      <div className="text-xs w-10 text-right">{row.otpPercentage}%</div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* CENTER column: map */}
        <div ref={mapContainerRef} className="relative bg-gray-50 rounded h-full">
          {(loadingGtfs || loadingStopTimes) && (
            <div className="absolute top-2 right-2 z-10 bg-white/90 rounded shadow px-3 py-1 flex items-center gap-2 text-xs">
              <Spin size="small" />
              <span>{loadingGtfs ? t('loading_routes_and_stops') : t('loading_schedule')}</span>
            </div>
          )}
          <GoogleMap
            id="ama-live-map"
            mapId="ama-live-map"
            defaultCenter={PR_CENTER}
            defaultZoom={PR_DEFAULT_ZOOM}
            mapTypeId={mapSettings?.type || 'roadmap'}
            disableDefaultUI
            style={{ width: '100%', height: '100%' }}
          >
            {visibleStops.map((stop) => (
              <AdvancedMarker
                key={stop.stopId}
                position={{ lat: stop.lat, lng: stop.lng }}
                onClick={() => { setSelectedStop(stop); setModalVehicleId(null); }}
              >
                <div
                  className="rounded-full border-2 border-white shadow"
                  style={{ width: 12, height: 12, background: stopStatusColor(stop) }}
                />
              </AdvancedMarker>
            ))}

            {filteredAssignments.map(({ vehicle, otp }) => {
              const color = otp ? (STATUS_COLORS[otp.status] || STATUS_COLOR_DEFAULT) : STATUS_COLOR_DEFAULT;
              const isStationary = !vehicle.isDriving || (vehicle.speedKmh ?? 0) < 1;
              const bearing = Number.isFinite(vehicle.bearing) ? vehicle.bearing : 0;
              return (
                <AdvancedMarker
                  key={vehicle.id}
                  position={{ lat: vehicle.lat, lng: vehicle.lng }}
                  onClick={() => focusVehicle(vehicle)}
                >
                  <div
                    className="rounded-full shadow flex items-center justify-center"
                    style={{ width: 26, height: 26, background: color, border: '2px solid white' }}
                    title={vehicle.name}
                  >
                    {isStationary ? (
                      <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />
                    ) : (
                      <FontAwesomeIcon
                        icon={faChevronUp}
                        style={{
                          color: 'white',
                          fontSize: 12,
                          transform: `rotate(${bearing}deg)`,
                          transformOrigin: 'center'
                        }}
                      />
                    )}
                  </div>
                </AdvancedMarker>
              );
            })}
          </GoogleMap>
        </div>

        {/* RIGHT column: live vehicle list */}
        <div className="bg-white rounded shadow-sm h-full flex flex-col overflow-hidden">
          <div className="p-2 border-b space-y-2">
            <div className="text-sm font-semibold">{t('vehicles')}</div>
            <div className="flex gap-2">
              <Input
                size="small"
                allowClear
                placeholder={t('search')}
                value={vehicleSearch}
                onChange={(e) => setVehicleSearch(e.target.value)}
              />
              <AntSelect
                size="small"
                value={vehicleTypeFilter}
                onChange={setVehicleTypeFilter}
                style={{ width: 110 }}
                options={[
                  { value: 'all', label: t('all') },
                  { value: 'bus', label: t('bus') },
                  { value: 'car', label: t('car') },
                  { value: 'other', label: t('other') }
                ]}
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {liveAssignments.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Empty description={t('waiting_for_vehicles')} />
              </div>
            ) : filteredAssignments.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Empty description={t('no_data')} />
              </div>
            ) : (
              filteredAssignments.map((a) => {
                const color = a.otp ? (STATUS_COLORS[a.otp.status] || STATUS_COLOR_DEFAULT) : STATUS_COLOR_DEFAULT;
                const bearing = Number.isFinite(a.vehicle.bearing) ? a.vehicle.bearing : 0;
                const isStationary = !a.vehicle.isDriving || (a.vehicle.speedKmh ?? 0) < 1;
                return (
                  <button
                    type="button"
                    key={a.vehicle.id}
                    className="w-full text-left px-3 py-2 border-b hover:bg-gray-50 flex items-start gap-3"
                    onClick={() => focusVehicle(a.vehicle)}
                  >
                    <div
                      className="rounded-full shadow flex items-center justify-center flex-shrink-0"
                      style={{ width: 28, height: 28, background: color, border: '2px solid white' }}
                    >
                      {isStationary ? (
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />
                      ) : (
                        <FontAwesomeIcon
                          icon={faChevronUp}
                          style={{ color: 'white', fontSize: 12, transform: `rotate(${bearing}deg)` }}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-blue-600 truncate flex items-center gap-1">
                        <span className="truncate">
                          {a.vehicle.name}{a.routeId ? `: ${t('route')} ${a.routeId}` : ''}
                        </span>
                        {a.vehicle.hasActiveFault && (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: '#dc2626' }}
                            title={t('fault_active')}
                          />
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {t(a.vehicleType || 'other')} · {formatSpeed(a.vehicle.speedKmh)}
                      </div>
                      {a.nextStop && (
                        <div className="text-xs text-gray-500 truncate" title={a.nextStop.stop.name}>
                          {t('next_stop')}: {a.nextStop.stop.name}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Bottom row: full route-analysis table — grows with content. */}
      <div className="bg-white rounded shadow-sm mx-2 mb-2 mt-4 p-2">
        {tableData.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Empty description={t('waiting_for_vehicles')} />
          </div>
        ) : (
          <Table
            size="small"
            rowKey="key"
            columns={tableColumns}
            dataSource={tableData}
            expandable={{ expandedRowRender }}
            rowClassName={(row) => (row.routeId === selectedRouteId ? 'bg-blue-50' : '')}
            pagination={{ pageSize: 50, hideOnSinglePage: true }}
          />
        )}
      </div>

      <Modal
        open={!!modalAssignment}
        onCancel={() => setModalVehicleId(null)}
        footer={null}
        width={640}
        title={modalAssignment ? `${t('vehicle')}: ${modalAssignment.vehicle.name}` : ''}
      >
        {modalAssignment && (() => {
          const v = modalAssignment.vehicle;
          const isBus = modalAssignment.vehicleType === 'bus';
          const isVehicleStopped = !v.isDriving || (v.speedKmh ?? 0) < 1;
          const faults = v.faults || [];

          const headerRow = (
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={VEHICLE_TYPE_ICONS[modalAssignment.vehicleType || 'other']} />
                <span className="text-gray-500">{t(modalAssignment.vehicleType || 'other')}</span>
              </div>
              {isBus && (
                <div>
                  <span className="text-gray-500">{t('route')}: </span>
                  <span className="font-semibold">
                    {modalAssignment.routeId || <span className="text-gray-400">{t('unknown')}</span>}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-500">{t('speed')}: </span>
                <span className="font-semibold">{formatSpeed(v.speedKmh)}</span>
              </div>
              {isBus && modalAssignment.otp && renderStatusTag(modalAssignment.otp.status)}
              {v.hasActiveFault && (
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {t('fault_active')}
                </span>
              )}
            </div>
          );

          const detailsSection = (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-gray-500 text-xs">{t('license_plate')}</div>
                <div className="font-mono">{v.licensePlate || <span className="text-gray-400">—</span>}</div>
              </div>
              <div>
                <div className="text-gray-500 text-xs">{t('vin')}</div>
                <div className="font-mono break-all">{v.vin || <span className="text-gray-400">—</span>}</div>
              </div>
              <div className="sm:col-span-3">
                <div className="text-gray-500 text-xs">{t('comment')}</div>
                <div className="whitespace-pre-wrap">{v.comment || <span className="text-gray-400">—</span>}</div>
              </div>
            </div>
          );

          const faultsSection = (
            <div>
              <div className="text-sm font-semibold mb-1">{t('faults')}</div>
              {faults.length === 0 ? (
                <div className="text-xs text-gray-400">{t('no_faults')}</div>
              ) : (
                <Table
                  size="small"
                  rowKey={(row) => row.id || `${row.diagnosticId}-${row.dateTime}`}
                  dataSource={faults}
                  pagination={false}
                  scroll={{ y: 200 }}
                  columns={[
                    {
                      title: t('diagnostic'),
                      key: 'diagnostic',
                      render: (obj, row) => (
                        <span title={row.diagnosticId}>
                          {row.diagnosticName || row.diagnosticCode || row.diagnosticId || '—'}
                        </span>
                      )
                    },
                    { title: t('severity'), dataIndex: 'severity', key: 'severity', width: 110, render: (s) => s || '—' },
                    {
                      title: t('state'),
                      dataIndex: 'faultState',
                      key: 'faultState',
                      width: 110,
                      render: (s, row) => row.isDismissed ? t('dismissed') : (s || '—')
                    },
                    {
                      title: t('time'),
                      dataIndex: 'dateTime',
                      key: 'dateTime',
                      width: 160,
                      render: (d) => d ? dayjs(d).format('MMM DD, YYYY hh:mm A') : '—'
                    }
                  ]}
                />
              )}
            </div>
          );

          if (!isBus) {
            return (
              <div className="space-y-3">
                {headerRow}
                {detailsSection}
                <div className="text-sm">
                  <div className="text-gray-500 text-xs">{t('coordinates')}</div>
                  <div className="font-mono">{v.lat?.toFixed?.(5)}, {v.lng?.toFixed?.(5)}</div>
                </div>
                {v.lastUpdate && (
                  <div className="text-xs text-gray-500">
                    {t('last_update')}: {dayjs(v.lastUpdate).format('MMM DD, YYYY hh:mm A')}
                  </div>
                )}
                {faultsSection}
              </div>
            );
          }

          return (
            <div className="space-y-3">
              {headerRow}
              {detailsSection}
              {isVehicleStopped && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-2">
                  {t('stopped_eta_note')}
                </div>
              )}
              <div>
                <div className="text-sm font-semibold mb-1">{t('upcoming_stops')}</div>
                {modalUpcomingStops.length === 0 ? (
                  <Empty description={t('no_data')} />
                ) : (
                  <Table
                    size="small"
                    rowKey="key"
                    dataSource={modalUpcomingStops}
                    pagination={false}
                    scroll={{ y: 240 }}
                    columns={[
                      { title: t('stop'), dataIndex: 'stopName', key: 'stopName' },
                      {
                        title: t('scheduled_time'),
                        dataIndex: 'scheduledMinutes',
                        key: 'scheduledMinutes',
                        width: 100,
                        render: formatGtfsClock
                      },
                      {
                        title: t('eta'),
                        dataIndex: 'etaMinutesFromNow',
                        key: 'etaMinutesFromNow',
                        width: 110,
                        render: formatEta
                      },
                      {
                        title: t('status'),
                        dataIndex: 'status',
                        key: 'status',
                        width: 110,
                        render: (s) => (s ? renderStatusTag(s) : <span className="text-gray-400">—</span>)
                      }
                    ]}
                  />
                )}
              </div>
              {faultsSection}
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={!!selectedStop}
        onCancel={() => setSelectedStop(null)}
        footer={null}
        width={520}
        title={selectedStop ? selectedStop.name : ''}
      >
        {selectedStop && (
          <div className="space-y-3 text-sm">
            <div className="text-xs text-gray-500">ID: {selectedStop.stopId}</div>
            <div>
              <div className="text-sm font-semibold mb-1">{t('next_bus')}</div>
              {stopModalNextBus ? (
                <div className="border rounded p-2 space-y-1">
                  <div className="font-semibold text-blue-600">
                    {t('route')} {stopModalNextBus.routeId} · {stopModalNextBus.vehicle.name}
                  </div>
                  <div>
                    <span className="text-gray-500">{t('eta')}: </span>
                    <span className="font-semibold">{formatEta(stopModalNextBus.etaMinutesFromNow)}</span>
                    {' · '}
                    <span className="text-gray-500">{t('scheduled_time')}: </span>
                    <span>{formatGtfsClock(stopModalNextBus.scheduledMinutes)}</span>
                  </div>
                  {stopModalNextBus.status && (
                    <div>{renderStatusTag(stopModalNextBus.status)}</div>
                  )}
                </div>
              ) : (
                <div className="text-gray-400">{t('no_upcoming_bus')}</div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default AmaDashboardLiveMap;
